import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ParkingFeeListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-fee',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-fee.component.html',
})
export class ModalParkingFeeComponent implements OnInit {
  @Input() editRow: ParkingFeeListRow | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingFeeListRow;
  feeGroups: Array<{ id: number; name: string }> = [];
  isSaving = signal(false);

  private readonly api = inject(ApiMasterFeeService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return (this.editRow?.id ?? 0) > 0;
  }

  ngOnInit(): void {
    this.formData = this.editRow
      ? { ...this.editRow }
      : this.mapper.createEmptyFee();
    this.loadFeeGroups();
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อ Fee');
      return;
    }

    const isEdit = this.isEdit;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = this.mapper.mapFeePayload(this.formData, userId, menuId);
    const request$ = isEdit
      ? this.api.updateParkingFee(this.formData.id, payload)
      : this.api.insertParkingFee(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadFeeGroups(): void {
    this.api.getParkingFeeGroupDropdown().subscribe({
      next: (res) => {
        this.feeGroups = this.mapper.mapIdNameList(res);
      },
      error: () => (this.feeGroups = []),
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return (this.editRow?.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
